'use client';

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getUserImages, deleteImage, type ImageMetadata } from '../lib/firebaseStorage';
import { Trash2, Download, Loader2 } from 'lucide-react';

export function UserImages() {
    const { user } = useAuth();
    const [images, setImages] = useState<ImageMetadata[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    useEffect(() => {
        if (user?.userId) {
            // 1. Try to load from cache first for instant display
            const cacheKey = `user_images_${user.userId}`;
            const cached = localStorage.getItem(cacheKey);

            if (cached) {
                try {
                    const parsed = JSON.parse(cached);
                    setImages(parsed);
                    setLoading(false); // Show cached immediately
                } catch (e) {
                    console.warn('Failed to parse cached images');
                }
            }

            // 2. Fetch fresh data in background
            fetchImages(cacheKey);
        }
    }, [user?.userId]);

    const fetchImages = async (cacheKey: string) => {
        try {
            // Only set loading true if we didn't have cache (checked via images state length or flag)
            // But here we might want to show a subtle indicator or just update silently 
            // If we have no images yet, show loading.
            setLoading(prev => prev && images.length === 0);

            setError(null);
            const userImages = await getUserImages();

            // Update state
            setImages(userImages);

            // Update cache
            localStorage.setItem(cacheKey, JSON.stringify(userImages));

            setLoading(false);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to load images';
            setError(errorMessage);
            console.error('[v0] Error fetching images:', err);
            setLoading(false);
        }
    };

    const handleDelete = async (imageId: string) => {
        if (!window.confirm('Are you sure you want to delete this image?')) {
            return;
        }

        try {
            setDeletingId(imageId);
            await deleteImage(imageId);
            setImages(prev => prev.filter(img => img.id !== imageId));
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to delete image';
            setError(errorMessage);
            console.error('[v0] Error deleting image:', err);
        } finally {
            setDeletingId(null);
        }
    };

    const handleDownload = async (url: string, fileName: string) => {
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
        } catch (err) {
            console.error('[v0] Error downloading image (likely CORS), falling back to open:', err);
            // Fallback for CORS issues: Open in new tab
            window.open(url, '_blank');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 text-primary animate-spin" />
                <span className="ml-2 text-muted-foreground">Loading your images...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
                <p className="font-medium">Error loading images</p>
                <p className="text-sm mt-1">{error}</p>
                <button
                    onClick={() => user?.userId && fetchImages(`user_images_${user.userId}`)}
                    className="mt-3 px-3 py-1.5 text-sm font-medium bg-red-100 hover:bg-red-200 rounded transition-colors"
                >
                    Try again
                </button>
            </div>
        );
    }

    if (images.length === 0) {
        return (
            <div className="text-center py-12">
                <p className="text-muted-foreground">No images saved yet</p>
                <p className="text-sm text-muted-foreground mt-1">
                    Process and save images to see them here
                </p>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {images.map((image) => (
                <div
                    key={image.id}
                    className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
                >
                    <div className="aspect-square overflow-hidden bg-background">
                        <img
                            src={image.processedUrl || image.originalUrl}
                            alt={image.fileName}
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        />
                    </div>
                    <div className="p-4">
                        <p className="text-sm font-medium text-foreground truncate">
                            {image.fileName}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                            {new Date(image.createdAt).toLocaleDateString()}
                        </p>
                        <div className="flex gap-2 mt-4">
                            <button
                                onClick={() =>
                                    handleDownload(
                                        image.processedUrl || image.originalUrl,
                                        image.fileName
                                    )
                                }
                                className="flex-1 px-3 py-2 text-xs font-medium bg-primary text-primary-foreground rounded hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                            >
                                <Download className="h-3 w-3" />
                                Download Now
                            </button>
                            <button
                                onClick={() => handleDelete(image.id)}
                                disabled={deletingId === image.id}
                                className="px-3 py-2 text-xs font-medium bg-red-50 text-red-600 rounded hover:bg-red-100 transition-colors disabled:opacity-50 flex items-center justify-center"
                            >
                                {deletingId === image.id ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                    <Trash2 className="h-3 w-3" />
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}
